import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../api/analytics';

/**
 * ページビューを自動的に記録するカスタムフック
 * 
 * 使用方法:
 * ```tsx
 * function MyComponent() {
 *   usePageView();
 *   return <div>...</div>;
 * }
 * ```
 * 
 * 注意:
 * - ページ遷移時に自動的にページビューを記録します
 * - エラーが発生してもユーザーには影響を与えません（コンソールに警告を出力）
 * - 同じページに複数回アクセスした場合でも、各アクセスを記録します
 * - 管理画面（/adminで始まるパス）は記録しません
 * - ルート（/）は記録しません（必ず /ja または /en にリダイレクトされるため二重計上を防ぐ）
 * - クエリパラメータ（?category=sampleなど）も含めて別ページとしてカウントします
 */
export const usePageView = (): void => {
  const location = useLocation();
  const hasTrackedRef = useRef<boolean>(false);
  const currentPathRef = useRef<string>('');
  const previousPathRef = useRef<string>('');

  useEffect(() => {
    // 管理画面（/adminで始まるパス）は記録しない
    if (location.pathname.startsWith('/admin')) {
      return;
    }

    // ルート（/）は常に /ja または /en にリダイレクトされるため記録しない（二重計上を防ぐ）
    if (location.pathname === '/') {
      return;
    }

    // パスとクエリパラメータを含めた完全なパスを生成
    const fullPath = location.pathname + location.search;

    // パスが変更された場合のみ記録（クエリパラメータの変更も含む）
    if (currentPathRef.current !== fullPath) {
      // 遷移前のパスを保存（現在のパスが存在する場合のみ）
      if (currentPathRef.current) {
        previousPathRef.current = currentPathRef.current;
      }
      currentPathRef.current = fullPath;
      hasTrackedRef.current = false;
    }

    // まだ記録していない場合のみ記録
    if (!hasTrackedRef.current) {
      hasTrackedRef.current = true;
      
      // 遷移前のパスを取得（存在する場合のみ）
      const previousPath = previousPathRef.current || '';
      
      
      // ページビューを記録（ルートリダイレクトで渡された state.referrer があれば第3引数で渡す）
      trackPageView(fullPath, previousPath, location.state?.referrer).catch((error) => {
        // エラーは既にtrackPageView内でログに記録されているため、
        // ここでは何もしない
        console.debug('Page view tracking error (ignored):', error);
      });
    }
  }, [location.pathname, location.search]);
};
